import z from "zod";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { Button, Input, Select } from "antd";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import Head from "../Head";
import { creatableRoles } from "../../data";
import styleUserCreate from "./UsersCreate.module.css";
import { useCreateUser, useGetEditors, useUpdateUser } from "../../api/users";
import { useGetLoggedInUser } from "../../api/loggedinUser";
import { useRHForm, ControlledField } from "../form";
import { ErrorLabel } from "../atomic";
import { isEditor, ROLES } from "../../utils/permissions";

const formatUserName = (user) => {
  if (!user) return "";
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  return name || user.email || user.username || "";
};

const UsersCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { data: loggedInUserRes } = useGetLoggedInUser();
  const currentUser = loggedInUserRes?.data;
  const currentUserIsEditor = isEditor(currentUser);
  const currentEditorId = currentUserIsEditor
    ? currentUser?.editor_id ?? currentUser?.id
    : undefined;
  const {
    mutateAsync: mutateAsyncCreate,
    isLoading: isLoadingCreate,
    error: errorCreate,
  } = useCreateUser();
  const {
    mutateAsync: mutateAsyncUpdate,
    isLoading: isLoadingUpdate,
    error: errorUpdate,
  } = useUpdateUser(id);
  const { data: editors = [], isLoading: isLoadingEditors } = useGetEditors({
    enabled: !currentUserIsEditor,
  });

  const { first_name, last_name, email, groups, organization, editor, editor_id } =
    state ?? {};

  const {
    Form,
    methods: {
      control,
      watch,
      setValue,
      formState: { errors },
    },
  } = useRHForm({
    initialValues: id
      ? {
          first_name,
          last_name,
          email,
          organization: organization ?? "",
          role: groups?.[0]?.role,
          editor_id: editor_id ?? editor?.id,
        }
      : {
          ...(currentUserIsEditor
            ? { role: ROLES.VIEWER, editor_id: currentEditorId }
            : {}),
        },
    schema: z
      .object({
        first_name: z.string().optional(),
        last_name: z.string().optional(),
        organization: z.string().optional(),
        role: z.string({ required_error: "Role is required." }),
        email: z.string({ required_error: "Username/email is required." }),
        editor_id: z.union([z.string(), z.number()]).optional().nullable(),
      })
      .superRefine((data, ctx) => {
        const missing =
          data.editor_id === undefined ||
          data.editor_id === null ||
          data.editor_id === "";
        if (data.role === ROLES.VIEWER && missing) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Editor is required.",
            path: ["editor_id"],
          });
        }
      }),
  });

  const selectedRole = watch("role");
  const showEditorField = !currentUserIsEditor && selectedRole === ROLES.VIEWER;
  const roleOptions = currentUserIsEditor
    ? creatableRoles.filter((role) => role.value === ROLES.VIEWER)
    : creatableRoles;
  const editorOptions = currentUserIsEditor
    ? [{ value: currentEditorId, label: formatUserName(currentUser) || "Current editor" }]
    : (editors || []).map((editorUser) => ({
        value: editorUser.id,
        label: formatUserName(editorUser),
      }));

  useEffect(() => {
    if (currentUserIsEditor) {
      setValue("role", ROLES.VIEWER);
      if (currentEditorId != null) setValue("editor_id", currentEditorId);
      return;
    }
    if (selectedRole !== ROLES.VIEWER) {
      setValue("editor_id", undefined);
    }
  }, [currentUserIsEditor, currentEditorId, selectedRole, setValue]);

  const handleSubmit = (data) => {
    const formData = new FormData();
    const roleToSend = currentUserIsEditor ? ROLES.VIEWER : data?.role;
    const editorIdToSend = currentUserIsEditor ? currentEditorId : data?.editor_id;

    if (data?.first_name) {
      formData.append("first_name", data?.first_name);
    }
    if (data?.last_name) {
      formData.append("last_name", data?.last_name);
    }
    if (roleToSend) {
      formData.append("role", roleToSend);
    }
    if (data?.email) {
      formData.append("email", data?.email);
    }
    formData.append("organization", data?.organization ?? "");
    if (roleToSend === ROLES.VIEWER && editorIdToSend) {
      formData.append("editor_id", String(editorIdToSend));
    }
    if (id) {
      mutateAsyncUpdate(formData)
        .then((res) => {
          if (res?.data) {
            toast.success("User updated successfully.");
            navigate("/web-twinprocms/users");
          }
        })
        .catch((error) => {
          toast.error(error?.message);
        });
      return;
    }

    mutateAsyncCreate(formData)
      .then((res) => {
        if (res?.data) {
          toast.success("User created successfully.");
          navigate("/web-twinprocms/users");
        }
      })
      .catch((error) => {
        toast.error(error?.message);
      });
  };

  return (
    <main className={styleUserCreate["user-create-main-container"]}>
      <Head title="Users" />
      <div className={styleUserCreate["user-create-title-container"]}>
        <h1>User</h1>
      </div>
      <section className={styleUserCreate["user-create-form-container"]}>
        <Form onSubmit={handleSubmit}>
          <ControlledField
            name="first_name"
            label="First Name"
            control={control}
            Component={Input}
            errors={errors}
          />
          <ErrorLabel>
            {(
              errorCreate || errorUpdate
            )?.response?.data?.first_name?.toString()}
          </ErrorLabel>
          <ControlledField
            name="last_name"
            label="Last Name"
            control={control}
            Component={Input}
            errors={errors}
          />
          <ErrorLabel>
            {(
              errorCreate || errorUpdate
            )?.response?.data?.last_name?.toString()}
          </ErrorLabel>
          <ControlledField
            required
            name="email"
            label="Username/Email"
            control={control}
            Component={Input}
            errors={errors}
          />
          <ErrorLabel>
            {(errorCreate || errorUpdate)?.response?.data?.email?.toString()}
          </ErrorLabel>
          <ControlledField
            name="organization"
            label="Organization"
            control={control}
            Component={Input}
            errors={errors}
          />
          <ErrorLabel>
            {(
              errorCreate || errorUpdate
            )?.response?.data?.organization?.toString()}
          </ErrorLabel>
          <ControlledField
            required
            name="role"
            label="Role"
            control={control}
            Component={Select}
            errors={errors}
            componentProps={{
              options: roleOptions,
              placeholder: "Select role",
              allowClear: !currentUserIsEditor,
              showSearch: true,
              disabled: currentUserIsEditor,
              filterOption: (inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >=
                0,
            }}
          />
          <ErrorLabel>
            {(errorCreate || errorUpdate)?.response?.data?.role?.toString()}
          </ErrorLabel>
          {currentUserIsEditor && errors?.editor_id && (
            <ErrorLabel>{errors.editor_id.message}</ErrorLabel>
          )}
          {showEditorField && (
            <>
              <ControlledField
                required
                name="editor_id"
                label="Editor"
                control={control}
                Component={Select}
                errors={errors}
                componentProps={{
                  options: editorOptions,
                  placeholder: "Select editor",
                  allowClear: !currentUserIsEditor,
                  showSearch: true,
                  loading: isLoadingEditors,
                  disabled: currentUserIsEditor,
                  filterOption: (inputValue, option) =>
                    (option?.label || "")
                      .toLowerCase()
                      .indexOf(inputValue.toLowerCase()) >= 0,
                }}
              />
              <ErrorLabel>
                {(
                  errorCreate || errorUpdate
                )?.response?.data?.editor_id?.toString()}
              </ErrorLabel>
            </>
          )}
          <div className={styleUserCreate["user-create-button-container"]}>
            <Button
              htmlType="submit"
              loading={isLoadingCreate || isLoadingUpdate}
              disabled={isLoadingCreate || isLoadingUpdate}
            >
              {!(isLoadingCreate || isLoadingUpdate) && (
                <FontAwesomeIcon icon={faCheck} />
              )}
              Submit
            </Button>
          </div>
        </Form>
      </section>
    </main>
  );
};

export default UsersCreate;
